import {
  Modal,
  useTranslation,
  Box,
  Text,
  Flex,
  Icon,
  IconButton,
  TextInput,
  Tag,
  Toggle,
  SingleHierarchySelector,
  TextArea,
  useToast,
  Link,
} from "@familyzone/component-library";
import React, { useMemo, useState } from "react";
import { AllOptions, FilteringRule, Signature } from "../../pages/filtering/types";
import {
  getPolicies,
  urlPolicySearch,
  urlSignatureSearch,
  validateDomain,
  modifyPolicy,
  reclassify,
} from "../../pages/filtering/ApiHelpers";
import SignatureHierarchy, { SignatureTree } from "./SignatureHierarchy";
import NoContentDisplay from "../../pages/filtering/NoContentDisplay";
import StatusChip from "./StatusChip";
import { convertSignature } from "../../pages/filtering/SignatureSelector";
import { Button } from "@chakra-ui/button";
import SessionStore from "../../stores/SessionStore";
import { colors } from "../../theme";

export const headerText = "Category Search";
export const headerDescText = "Check the category that a website exists in.";
export const websiteLabelText = "Website";
export const categoryLabelText = "Search for a Signature or Category";
export const policyLabelText = "Matching Policies";
export const websitePlaceholderText = "e.g. youtube.com";
export const noResultsText = "Uncategorised Website";
export const blockedText = "Blocked";
export const allowedText = "Allowed";
export const enabledText = "Enabled";
export const disabledText = "Disabled";
export const noResultsDescription = "You can suggest a category for this website below.";
export const reviewText = "If you believe this website should be in a different category, please contact our support team.";
export const reclassifyText = "Suggest a different category";
export const reclassifynewText = "Suggest a category";
export const reclassifyIntroText = "Send your request below to suggest a different category for the related website.";
export const reclassifynewIntoText = "Send your request below to suggest a category for the related website";
export const signatureSelectLabelText = "Select a category";
export const reasonLabelText = "Provide a reason for the Request";
export const requestSent = "Request Sent";
export const requestSentDesc =
  "Thank you for your suggestion, our team will review your request. To check the status, please reach out to the Support Team.";
export interface SignatureSearchModalProps {
  open: boolean;
  onClose: () => void;
  onSignatureClick?: (rule: FilteringRule) => void;
  signatures?: Signature[];
  onRefresh?: () => void;
}

const SignatureSearchModal: React.FC<SignatureSearchModalProps> = ({
  open,
  onClose,
  onSignatureClick = () => "",
  signatures,
  onRefresh = () => void {},
}) => {
  const { t } = useTranslation();

  const [urlErrorText, setURLErrorText] = useState("");
  const [url, setUrl] = useState("");
  const [isLoaded, setIsLoaded] = useState(true);
  const [signaturesResult, setSignaturesResult] = useState<Signature[]>([]);
  const [submittedUrl, setSubmittedUrl] = useState("");
  const [rulesResult, setRulesResult] = useState<FilteringRule[]>([]);
  const [showReclassify, setShowReclassify] = useState(false);
  const [completedReclassify, setCompletedReclassify] = useState(false);
  const [selectedSignature, setSelectedSignature] = useState<AllOptions | null>(null);
  const [reasonValue, setReasonValue] = useState("");
  const [reasonErrorText, setReasonErrorText] = useState("");
  const { errorToast } = useToast();

  const sortedSignatures = (signatures ?? []).sort((a, b) =>
    a.name.toLowerCase() > b.name.toLowerCase() ? 1 : b.name.toLowerCase() > a.name.toLowerCase() ? -1 : 0
  );

  const options: AllOptions[] = useMemo(
    () => [
      ...sortedSignatures
        .filter(
          (s) => !s.hidden && s.enabled && s.id !== "sphirewall.application.alwaysdeny" && s.id !== "sphirewall.application.alwaysallow"
        )
        .map((s) => convertSignature(s, t)),
    ],
    [sortedSignatures, t]
  );

  const handleChangeURL = (e: React.ChangeEvent<HTMLInputElement>) => {
    // https://stackoverflow.com/a/49849482
    if (validateDomain(e.target.value)) {
      setURLErrorText("Invalid url entered");
    } else {
      setURLErrorText("");
    }

    setUrl(e.target.value);
  };

  const populateResult = async () => {
    try {
      setIsLoaded(false);
      const newUrl = url.replace("https://", "").replace("http://", "").replace("www.", "").split("/")[0].split(`\\`)[0].toLowerCase();
      const result = await urlSignatureSearch(newUrl);
      const matchingSignatures: Signature[] = [];
      if (signatures) {
        if ("signature" in result) {
          const signatureDetails = signatures.find((s) => s.id === result.signature);
          if (signatureDetails) matchingSignatures.push(signatureDetails);
        }
        if ("category" in result) {
          const signatureDetails = signatures.find((s) => s.id === result.category);
          if (signatureDetails) matchingSignatures.push(signatureDetails);
        }
        if ("subCategory" in result) {
          const signatureDetails = signatures.find((s) => s.id === result.subCategory);
          if (signatureDetails) matchingSignatures.push(signatureDetails);
        }
      }
      const matchingPolicy = await urlPolicySearch(newUrl);
      setUrl(newUrl);
      setSubmittedUrl(newUrl);
      setSignaturesResult(matchingSignatures);
      setRulesResult(matchingPolicy.matching_rules);
      setCompletedReclassify(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoaded(true);
    }
  };

  const handlePopulateResult = () => void populateResult();

  const getSignatureList = (): Signature[] => {
    const category: Signature | undefined = signaturesResult.find((s) => s.is_category);
    const subCategory: Signature | undefined = signaturesResult.find((s) => s.is_sub_category);
    const signature: Signature | undefined = signaturesResult.find((s) => !s.is_category && !s.is_sub_category);

    const localSignatureList: Signature[] = [];
    if (category) {
      localSignatureList.push(category);
    }
    if (subCategory) {
      localSignatureList.push(subCategory);
    }
    if (signature) {
      localSignatureList.push(signature);
    }
    return localSignatureList;
  };

  const onToggleEnabled = (rule: FilteringRule) =>
    void (async () => {
      try {
        if (!rule) return;

        setIsLoaded(false);
        const allRules = await getPolicies(false);
        const currentRule = allRules.find((r) => r.id === rule?.id);
        if (!currentRule) {
          return;
        }

        await modifyPolicy({
          ...currentRule,
          enabled: !currentRule.enabled,
        });

        setRulesResult(rulesResult.map((r) => (r.id === rule.id ? { ...r, enabled: !r.enabled } : r)));

        onRefresh();
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoaded(true);
      }
    })();

  const handleChangeSignature = (selected: AllOptions | null) => {
    if (selected) {
      if (signatures?.find((sig) => sig.id === selected.value)?.is_category === true) {
        setSelectedSignature(null);
      } else {
        setSelectedSignature(selected);
      }
    }
  };

  const handleChangeReasonValue = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReasonErrorText("");
    setReasonValue(e.target.value);
  };

  const handleConfirmSuggestion = async () => {
    try {
      setIsLoaded(false);
      if (!selectedSignature) return;

      if (reasonValue.length < 25) {
        setReasonErrorText("Please provide a reason for the suggestion (minimum 25 characters)");
        return;
      }
      /* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */
      const email: string = SessionStore.getUsername();
      const device_name: string = SessionStore.getSelectedDeviceName();
      const device_id: string = SessionStore.getSelectedDevice();
      const region: string = SessionStore.getSelectedRegion();
      /* eslint-enable */

      const result = await reclassify(
        url,
        reasonValue,
        signatures?.find((sig) => sig.id === selectedSignature.value),
        email,
        device_name,
        device_id,
        region
      );

      if (result.result !== "") {
        errorToast({
          title: t("Error"),
          description: t(result.result),
        });
        return;
      }

      setCompletedReclassify(true);
      setSelectedSignature(null);
      setReasonValue("");
    } catch (err) {
      errorToast({
        title: t("Error submitting suggestion"),
        description: t("An error occurred while sending the suggestion, please contact support."),
      });
    } finally {
      setIsLoaded(true);
    }
  };

  const signatureList = getSignatureList();

  const getSignatureTree = (): SignatureTree[] => {
    const tree: SignatureTree[] = [];

    signatureList.forEach((s, i) => {
      if (i === 0) {
        tree.push({
          name: s.name,
          id: s.id,
          icon: s.favicon_url,
          important: false,
          included: true,
          action: -1,
          children: [],
        });
      }

      if (i === 1) {
        tree[0].included = false;
        tree[0].children.push({
          name: s.name,
          id: s.id,
          icon: s.favicon_url,
          important: false,
          included: true,
          action: -1,
          children: [],
        });
      }

      if (i === 2) {
        tree[0].children[0].included = false;
        tree[0].children[0].children.push({
          name: s.name,
          id: s.id,
          icon: s.favicon_url,
          important: false,
          included: true,
          action: -1,
          children: [],
        });
      }
    });

    return tree;
  };

  const sortedRules: FilteringRule[] = useMemo(() => {
    const sticky = rulesResult.filter((r) => r.sticky);
    const nonSticky = rulesResult.filter((r) => !r.sticky);
    return [...sticky, ...nonSticky];
  }, [rulesResult]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handlePopulateResult();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal isOpen={open} onClose={handleClose} headerText={headerText} size="md">
      <Flex flexDirection="column" overflowY="auto" maxHeight="90vh" pr="sp16">
        <Box pt="sp24">
          <Text color="text.paragraph.regular">{headerDescText}</Text>
        </Box>
        <Flex pt="sp24">
          <Text color="text.paragraph.regular" fontSize="sm">
            {t(websiteLabelText)}
          </Text>
        </Flex>
        <Flex pt="sp4" direction="row" alignItems="center">
          <Box flexGrow={1}>
            <form onSubmit={handleSubmit}>
              <TextInput
                aria-label={t(websiteLabelText)}
                placeholder={t(websitePlaceholderText)}
                onChange={handleChangeURL}
                value={url}
                isDisabled={!isLoaded}
              />
            </form>
          </Box>
          <Box pl="sp16">
            <IconButton
              icon={<Icon icon="fa-search" />}
              variant="primary"
              onClick={handlePopulateResult}
              aria-label="Search"
              size="default"
              disabled={!isLoaded || !url}
              data-testid="signature-search-btn"
            />
          </Box>
        </Flex>
        <Flex pb="sp24" pt="sp4">
          <Text color="accent.red.500" fontSize="sm">
            {urlErrorText}
          </Text>
        </Flex>

        {!!submittedUrl && signaturesResult.length !== 0 && (
          <Flex>
            <Text pb="sp8">{t(categoryLabelText)}</Text>
          </Flex>
        )}

        <SignatureHierarchy signatureTree={getSignatureTree()} />

        {!!submittedUrl && (
          <Flex pb="sp8" mt="sp12" direction="column" flexGrow={1}>
            <Flex pt="sp12" pb="sp12">
              <Link
                color={colors.brand["400"]}
                onClick={() => {
                  setShowReclassify(!showReclassify);
                }}
                size="14px"
                data-testid="reclassify-link"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    setShowReclassify(!showReclassify);
                  }
                }}
                role="button"
                aria-label={t(reclassifyText)}
              >
                <Box flexGrow={1}>
                  {!showReclassify && <Icon icon={"fa-chevron-right"} ml="sp5" mr={5} pb={3} color={colors.brand["400"]} />}
                  {showReclassify && <Icon icon={"fa-chevron-down"} ml="sp5" mr={5} pb={3} color={colors.brand["400"]} />}
                  {signaturesResult.length !== 0 ? t(reclassifyText) : t(reclassifynewText)}
                </Box>
              </Link>
            </Flex>
            {showReclassify && !completedReclassify && (
              <>
                <Flex width="100%" p="sp8">
                  <Text color="text.paragraph.regular" fontSize="14px">
                    {signaturesResult.length !== 0 ? t(reclassifyIntroText) : t(reclassifynewIntoText)}
                  </Text>
                </Flex>
                <Flex>
                  <Text fontWeight="bold" p="sp8">
                    Category
                  </Text>
                </Flex>
                <Flex width="100%" position="relative" p="sp8">
                  <Box width="100%" overflowY="auto" py="sp4" overflow="visible">
                    <SingleHierarchySelector
                      onChange={(selected: AllOptions | null) => handleChangeSignature(selected)}
                      options={options}
                      selected={selectedSignature}
                      placeholder={t(categoryLabelText)}
                      name="category"
                      id="category"
                      disableCategorySelectionOptions={true}
                    />
                  </Box>
                </Flex>
                <Flex p="sp8">
                  <Text fontWeight="bold">Reason for request</Text>
                </Flex>
                <Flex p="sp8">
                  <Box width="100%">
                    <TextArea
                      placeholder={reasonLabelText}
                      rows={4}
                      onChange={handleChangeReasonValue}
                      value={reasonValue}
                      errorMessage={reasonErrorText}
                    />
                  </Box>
                </Flex>
                <Flex p="sp8">
                  <Button
                    mt="sp8"
                    variant="primary"
                    onClick={() => {
                      void handleConfirmSuggestion();
                    }}
                    disabled={!selectedSignature || !reasonValue || !isLoaded}
                  >
                    Send Suggestion
                  </Button>
                </Flex>
              </>
            )}
            {showReclassify && !!completedReclassify && (
              <Box backgroundColor={colors.accent.green["100"]} width="100%">
                <Flex p="sp8" direction="row">
                  <Flex p="sp8">
                    <Icon icon={"fa-circle-check"} color="green" height="17px" width="16px" />
                  </Flex>
                  <Flex direction="column">
                    <Flex>
                      <Text fontWeight="bold">{t(requestSent)}</Text>
                    </Flex>
                    <Flex>
                      <Text>{t(requestSentDesc)}</Text>
                    </Flex>
                  </Flex>
                </Flex>
              </Box>
            )}
          </Flex>
        )}

        {!!submittedUrl && rulesResult.length !== 0 && (
          <Flex pt="sp24">
            <Text>{t(policyLabelText)}</Text>
          </Flex>
        )}

        {!!submittedUrl && signaturesResult.length === 0 && (
          <Box display="flex" justifyContent="center" alignItems="center">
            <NoContentDisplay title={t(noResultsText)} text={t(noResultsDescription)}></NoContentDisplay>
          </Box>
        )}

        {!!submittedUrl && sortedRules.length !== 0 && (
          <Box minWidth="250px" pt="sp4" flexDirection="column">
            {sortedRules.map((rule) => (
              <Flex
                key={rule.id}
                mb="sp8"
                flexDirection="row"
                justifyContent="flex-start"
                alignItems="center"
                p="sp8"
                backgroundColor="neutrals.10"
                borderRadius={16}
                mt="10px"
              >
                <Flex>
                  <Toggle
                    data-testid={`${rule.id}-toggle-testpolicy`}
                    isChecked={rule.enabled}
                    onChange={() => onToggleEnabled(rule)}
                    aria-label="Policy Enabled"
                    disabled={!isLoaded}
                  />
                </Flex>
                <Flex>
                  <Text
                    fontWeight="medium"
                    whiteSpace="nowrap"
                    ml="sp8"
                    mr="sp16"
                    textOverflow="ellipsis"
                    maxWidth="300px"
                    overflow="hidden"
                    flexGrow={1}
                  >
                    {rule.name}
                  </Text>
                </Flex>
                <Flex justifyContent="space-between" alignItems="center" width="100%">
                  <Flex>
                    <StatusChip action={rule.action} size="small" lineHeight="20px" marginRight="sp4" />
                    {rule.sticky && <Tag variant="subtle" tagLeftIcon="fa fa-lock" tagLabel="Locked" size="compact" />}
                  </Flex>
                </Flex>
                <IconButton
                  icon={<Icon icon="fa-pencil" sx={{ color: "text.paragraph.light" }} />}
                  onClick={() => {
                    onSignatureClick(rule);
                  }}
                  variant="ghost"
                  aria-label="small-icon-search"
                  size="sm"
                  disabled={!isLoaded || !url}
                  aria-disabled={!isLoaded || !url}
                />
              </Flex>
            ))}
          </Box>
        )}

        {/* {!!submittedUrl && signaturesResult.length !== 0 && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            backgroundColor="neutrals.10"
            p="sp16"
            borderRadius={16}
            mt="sp16"
          >
            <Text textAlign="center" color="neutrals.400" maxWidth={280}>
              {t(reviewText)}
            </Text>
          </Box>
        )} */}
      </Flex>
    </Modal>
  );
};

export default SignatureSearchModal;
